import { Injectable, computed, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

export interface Breadcrumb {
    label: string;
    url?: string;
    queryParams?: Record<string, string>;
}

@Injectable({ providedIn: 'root' })
export class BreadcrumbService {
    private readonly breadcrumbs = signal<Breadcrumb[]>([]);
    private manualOverride = false;

    readonly currentBreadcrumbs = this.breadcrumbs.asReadonly();
    readonly currentTitle = computed(() => {
        const crumbs = this.breadcrumbs();
        return crumbs.length > 0 ? crumbs[crumbs.length - 1].label : '';
    });

    constructor(private router: Router) {
        this.router.events
            .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
            .subscribe(() => {
                if (!this.manualOverride) {
                    this.buildFromRoute();
                }
                this.manualOverride = false;
            });
    }

    /** Manually set breadcrumbs (overrides auto-build for this navigation). */
    set(crumbs: Breadcrumb[]): void {
        this.manualOverride = true;
        this.breadcrumbs.set(crumbs);
    }

    /** Clear all breadcrumbs. */
    clear(): void {
        this.breadcrumbs.set([]);
    }

    /** Update the label of the last breadcrumb (e.g. replace "Edit" with entity name). */
    updateLastLabel(label: string): void {
        const current = this.breadcrumbs();
        if (current.length === 0) return;
        const updated = [...current];
        updated[updated.length - 1] = { ...updated[updated.length - 1], label };
        this.breadcrumbs.set(updated);
    }

    getCurrentBreadcrumbs(): Breadcrumb[] {
        return this.breadcrumbs();
    }

    private buildFromRoute(): void {
        let route = this.router.routerState.root;
        const crumbs: Breadcrumb[] = [];
        let url = '';

        while (route) {
            if (route.snapshot.url.length > 0) {
                url += '/' + route.snapshot.url.map(s => s.path).join('/');
            }

            const data = route.snapshot.data;

            // Support array of breadcrumbs on the route data
            if (data['breadcrumbs']) {
                const routeCrumbs = data['breadcrumbs'] as Breadcrumb[];
                crumbs.push(...routeCrumbs);
            } else if (data['breadcrumb']) {
                crumbs.push({ label: data['breadcrumb'], url });
            }

            route = route.firstChild!;
        }

        // Last breadcrumb has no URL (current page)
        if (crumbs.length > 0) {
            crumbs[crumbs.length - 1] = { ...crumbs[crumbs.length - 1], url: undefined };
        }

        this.breadcrumbs.set(crumbs);
    }
}
